import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Page, PageHeader } from "@/components/ui-kit";
import { toast } from "sonner";
import { Save, Plus, Trash2, Home, Sparkles, Store, MessageSquare, HelpCircle, Phone } from "lucide-react";

const DEFAULT_CMS = {
  hero: {
    badge: "",
    titolo1: "",
    titolo2: "",
    subtitle: "",
    cta_primary: "",
    cta_secondary: "",
    image_url: "",
  },
  top_strip: { attivo: true, testo: "" },
  valori_titolo: "",
  valori_intro: "",
  servizi_titolo: "",
  servizi_intro: "",
  processo_titolo: "",
  processo_intro: "",
  faq_titolo: "",
  testimonial_titolo: "",
  contatti_titolo: "",
  contatti_intro: "",
  negozi_in_apertura: [],
};

function Field({ label, value, onChange, textarea = false, placeholder = "", testid }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-1">{label}</div>
      {textarea ? (
        <textarea
          className="w-full text-sm border border-zinc-300 rounded-lg px-3 py-2 focus:border-[#1FAE52] focus:outline-none"
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
        />
      ) : (
        <input
          className="w-full text-sm border border-zinc-300 rounded-lg px-3 py-2 focus:border-[#1FAE52] focus:outline-none"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
        />
      )}
    </label>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[#1FAE52]" />
        <h3 className="text-sm font-bold uppercase tracking-wide text-zinc-700">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export default function AdminLandingCMS() {
  const [cms, setCms] = useState(DEFAULT_CMS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/landing-cms")
      .then((r) => setCms({ ...DEFAULT_CMS, ...(r.data || {}) }))
      .catch((e) => toast.error("Errore caricamento: " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/landing-cms", cms);
      toast.success("Contenuti homepage salvati");
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const updHero = (k, v) => setCms((c) => ({ ...c, hero: { ...c.hero, [k]: v } }));
  const updStrip = (k, v) => setCms((c) => ({ ...c, top_strip: { ...c.top_strip, [k]: v } }));
  const addNegozio = () => setCms((c) => ({
    ...c,
    negozi_in_apertura: [...(c.negozi_in_apertura || []), { nome: "", citta: "", indirizzo: "", telefono: "", data_apertura: "" }],
  }));
  const updNegozio = (i, k, v) => setCms((c) => {
    const list = [...(c.negozi_in_apertura || [])];
    list[i] = { ...list[i], [k]: v };
    return { ...c, negozi_in_apertura: list };
  });
  const delNegozio = (i) => setCms((c) => ({
    ...c,
    negozi_in_apertura: (c.negozi_in_apertura || []).filter((_, idx) => idx !== i),
  }));

  return (
    <div>
      <PageHeader
        title="Homepage — Contenuti pubblici"
        subtitle="Testi, hero, sezioni e negozi in apertura del sito pubblico"
        actions={
          <button
            onClick={save}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 bg-[#1FAE52] hover:bg-[#168540] text-white px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50"
            data-testid="cms-save"
          >
            <Save size={14} /> {saving ? "Salvataggio…" : "Salva modifiche"}
          </button>
        }
      />
      <Page>
        {loading ? (
          <div className="text-center text-zinc-500 py-12">Caricamento…</div>
        ) : (
          <>
            {/* Banner top strip */}
            <Section icon={Sparkles} title="Banner in cima al sito" testid="cms-section-strip">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!cms.top_strip?.attivo}
                  onChange={(e) => updStrip("attivo", e.target.checked)}
                  data-testid="cms-strip-attivo"
                />
                Mostra il banner
              </label>
              <Field label="Testo del banner" value={cms.top_strip?.testo} onChange={(v) => updStrip("testo", v)} testid="cms-strip-testo" placeholder="es. 2 NUOVI SHOWROOM IN ARRIVO" />
              <p className="text-xs text-zinc-500">Il banner mostra anche la lista dei negozi in apertura, se ne aggiungi almeno uno qui sotto.</p>
            </Section>

            {/* Hero */}
            <Section icon={Home} title="Hero (intestazione principale)" testid="cms-section-hero">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Badge (piccolo tag sopra il titolo)" value={cms.hero.badge} onChange={(v) => updHero("badge", v)} testid="cms-hero-badge" />
                <Field label="Immagine hero (URL)" value={cms.hero.image_url} onChange={(v) => updHero("image_url", v)} testid="cms-hero-image" />
                <Field label="Titolo — parte 1" value={cms.hero.titolo1} onChange={(v) => updHero("titolo1", v)} testid="cms-hero-titolo1" />
                <Field label="Titolo — parte 2 (accento italic verde)" value={cms.hero.titolo2} onChange={(v) => updHero("titolo2", v)} testid="cms-hero-titolo2" />
              </div>
              <Field label="Sottotitolo" value={cms.hero.subtitle} onChange={(v) => updHero("subtitle", v)} textarea testid="cms-hero-subtitle" />
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="CTA primario (bottone verde)" value={cms.hero.cta_primary} onChange={(v) => updHero("cta_primary", v)} testid="cms-hero-cta-primary" />
                <Field label="CTA secondario (link)" value={cms.hero.cta_secondary} onChange={(v) => updHero("cta_secondary", v)} testid="cms-hero-cta-secondary" />
              </div>
            </Section>

            {/* Valori */}
            <Section icon={Sparkles} title="Sezione Valori" testid="cms-section-valori">
              <Field label="Titolo sezione" value={cms.valori_titolo} onChange={(v) => setCms({ ...cms, valori_titolo: v })} testid="cms-valori-titolo" />
              <Field label="Intro (paragrafo sotto il titolo)" value={cms.valori_intro} onChange={(v) => setCms({ ...cms, valori_intro: v })} textarea testid="cms-valori-intro" />
            </Section>

            {/* Servizi */}
            <Section icon={Home} title="Sezione Servizi" testid="cms-section-servizi">
              <Field label="Titolo sezione" value={cms.servizi_titolo} onChange={(v) => setCms({ ...cms, servizi_titolo: v })} testid="cms-servizi-titolo" />
              <Field label="Intro" value={cms.servizi_intro} onChange={(v) => setCms({ ...cms, servizi_intro: v })} textarea testid="cms-servizi-intro" />
            </Section>

            {/* Processo */}
            <Section icon={Home} title="Sezione Come Funziona (Processo)" testid="cms-section-processo">
              <Field label="Titolo sezione" value={cms.processo_titolo} onChange={(v) => setCms({ ...cms, processo_titolo: v })} testid="cms-processo-titolo" />
              <Field label="Intro" value={cms.processo_intro} onChange={(v) => setCms({ ...cms, processo_intro: v })} textarea testid="cms-processo-intro" />
            </Section>

            {/* Testimonial + FAQ + Contatti */}
            <Section icon={MessageSquare} title="Testimonial" testid="cms-section-testimonial">
              <Field label="Titolo sezione" value={cms.testimonial_titolo} onChange={(v) => setCms({ ...cms, testimonial_titolo: v })} testid="cms-testimonial-titolo" />
            </Section>
            <Section icon={HelpCircle} title="FAQ" testid="cms-section-faq">
              <Field label="Titolo sezione" value={cms.faq_titolo} onChange={(v) => setCms({ ...cms, faq_titolo: v })} testid="cms-faq-titolo" />
            </Section>
            <Section icon={Phone} title="Contatti" testid="cms-section-contatti">
              <Field label="Titolo sezione" value={cms.contatti_titolo} onChange={(v) => setCms({ ...cms, contatti_titolo: v })} testid="cms-contatti-titolo" />
              <Field label="Intro" value={cms.contatti_intro} onChange={(v) => setCms({ ...cms, contatti_intro: v })} textarea testid="cms-contatti-intro" />
            </Section>

            {/* Negozi in apertura */}
            <Section icon={Store} title="Negozi in apertura" testid="cms-section-negozi">
              <p className="text-xs text-zinc-500">
                Se vuoto, la sezione non appare sul sito pubblico. Il banner in cima al sito mostrerà le città listate qui.
              </p>
              {(cms.negozi_in_apertura || []).length === 0 && (
                <div className="text-center text-zinc-400 py-4 text-sm border border-dashed border-zinc-200 rounded-lg">
                  Nessun negozio in apertura configurato
                </div>
              )}
              {(cms.negozi_in_apertura || []).map((n, i) => (
                <div key={i} className="grid md:grid-cols-5 gap-2 items-end border border-zinc-200 rounded-lg p-3" data-testid={`cms-negozio-${i}`}>
                  <Field label="Nome" value={n.nome} onChange={(v) => updNegozio(i, "nome", v)} testid={`cms-negozio-nome-${i}`} placeholder="es. Bra" />
                  <Field label="Città" value={n.citta} onChange={(v) => updNegozio(i, "citta", v)} testid={`cms-negozio-citta-${i}`} placeholder="es. Cuneo" />
                  <Field label="Indirizzo" value={n.indirizzo} onChange={(v) => updNegozio(i, "indirizzo", v)} testid={`cms-negozio-indirizzo-${i}`} />
                  <Field label="Telefono" value={n.telefono} onChange={(v) => updNegozio(i, "telefono", v)} testid={`cms-negozio-tel-${i}`} />
                  <div className="flex items-end gap-2">
                    <Field label="Apertura" value={n.data_apertura} onChange={(v) => updNegozio(i, "data_apertura", v)} testid={`cms-negozio-data-${i}`} placeholder="Marzo 2026" />
                    <button
                      onClick={() => delNegozio(i)}
                      className="mb-0.5 text-rose-600 hover:bg-rose-50 p-2 rounded-lg"
                      data-testid={`cms-negozio-del-${i}`}
                      title="Rimuovi"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={addNegozio}
                className="inline-flex items-center gap-2 text-sm bg-zinc-100 hover:bg-zinc-200 px-3 py-2 rounded-lg"
                data-testid="cms-negozio-add"
              >
                <Plus size={14} /> Aggiungi negozio in apertura
              </button>
            </Section>

            <div className="sticky bottom-4 mt-6">
              <button
                onClick={save}
                disabled={saving}
                className="w-full bg-[#1FAE52] hover:bg-[#168540] text-white px-4 py-3 rounded-lg text-sm font-bold shadow-lg disabled:opacity-50"
                data-testid="cms-save-bottom"
              >
                <Save size={14} className="inline mr-2" />{saving ? "Salvataggio…" : "Salva tutti i contenuti"}
              </button>
            </div>
          </>
        )}
      </Page>
    </div>
  );
}
